import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Shield, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

export default function VerificationLookup() {
  const [id, setId] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Basic UUID validation
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(id.trim())) {
      setError('Please enter a valid Verification ID (UUID format)');
      return;
    }

    navigate(`/verify/${id.trim()}`);
  };

  return (
    <div className="min-h-screen bg-[#070b14] flex flex-col items-center justify-center px-4 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-600/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-indigo-600/10 blur-[120px] rounded-full" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-xl w-full relative z-10"
      >
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-blue-500/10 border border-blue-500/20 mb-6">
            <Shield className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-4 tracking-tight">
            Verification Trust Portal
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed">
            Verify the legitimacy of an Agos Verification Report. Enter the unique ID found on the report.
          </p>
        </div>

        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 p-8 rounded-3xl shadow-2xl">
          <form onSubmit={handleSearch} className="space-y-6">
            <div>
              <label htmlFor="verify-id" className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-3">
                Verification ID
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-blue-400 transition-colors">
                  <Search className="w-5 h-5" />
                </div>
                <input
                  id="verify-id"
                  type="text"
                  value={id}
                  onChange={(e) => setId(e.target.value)}
                  placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
                  className="block w-full pl-12 pr-4 py-4 bg-slate-950/50 border border-slate-800 rounded-2xl text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all font-mono text-sm"
                  required
                />
              </div>
              {error && (
                <p className="mt-3 text-sm text-red-400 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-black rounded-2xl transition-all shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40 group active:scale-[0.98]"
            >
              Verify Record
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 rounded-2xl bg-slate-900/40 border border-slate-800/40">
            <h3 className="text-white font-bold mb-2">Privacy First</h3>
            <p className="text-sm text-slate-400">
              Only cryptographic proofs and metadata are shown. Personal information remains private.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-slate-900/40 border border-slate-800/40">
            <h3 className="text-white font-bold mb-2">Tamper-Proof Proof</h3>
            <p className="text-sm text-slate-400">
              Unique cryptographic signatures ensure that the verification data has not been altered since creation.
            </p>
          </div>
        </div>

        <div className="mt-12 text-center text-slate-500 text-xs font-bold uppercase tracking-widest">
          Trusted by Banks & Government Agencies
        </div>
      </motion.div>
    </div>
  );
}
